import { useEffect, useState } from "react";
import { Cloud, CloudOff, RefreshCw, AlertTriangle } from "lucide-react";
import {
  fetchRemoteProgress,
  loadSyncState,
  schedulePush,
  type SyncState,
} from "@/lib/sync";
import {
  loadAllProgress,
  loadMeta,
  hydrateLocalFromRemote,
  setRemotePush,
} from "@/lib/progress";

let bootstrapped = false;

/**
 * Bootstrap the sync layer exactly once per page load. Renders nothing.
 * Mount near the root of the app (App.tsx).
 */
export function SyncBootstrap() {
  useEffect(() => {
    if (bootstrapped) return;
    bootstrapped = true;
    setRemotePush(payload => schedulePush(payload));
    (async () => {
      try {
        const remote = await fetchRemoteProgress();
        if (!remote) return;
        const localMeta = loadMeta();
        const localUpdatedAt = localMeta.lastActiveAt ?? new Date(0).toISOString();
        const remoteIsNewer = remote.updatedAt > localUpdatedAt;
        const remoteHasData = Object.keys(remote.chapters ?? {}).length > 0;
        const localHasData = Object.keys(loadAllProgress()).length > 0;
        if (remoteIsNewer && remoteHasData) {
          hydrateLocalFromRemote({ meta: remote.meta, chapters: remote.chapters });
        } else if (localHasData && !remoteHasData) {
          schedulePush({ meta: loadMeta(), chapters: loadAllProgress() }, 200);
        }
      } catch {
        // sync.ts already captured the error in state
      }
    })();
  }, []);
  return null;
}

export function SyncStatus() {
  const [state, setState] = useState<SyncState>(() => loadSyncState());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onChange = (e: Event) => {
      setState((e as CustomEvent<SyncState>).detail ?? loadSyncState());
    };
    window.addEventListener("aba-sync-change", onChange);
    return () => window.removeEventListener("aba-sync-change", onChange);
  }, []);

  const onClick = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const remote = await fetchRemoteProgress();
      if (remote && Object.keys(remote.chapters ?? {}).length > 0) {
        hydrateLocalFromRemote({ meta: remote.meta, chapters: remote.chapters });
      }
    } catch {
      // ignore
    } finally {
      setState(loadSyncState());
      setBusy(false);
    }
  };

  const enabled = state.enabled;
  const error = state.lastError;
  const label = busy
    ? "Syncing"
    : enabled
    ? state.email
      ? `Synced as ${state.email}`
      : "Synced"
    : error === "unauthorized"
    ? "Sync needs Access sign-in"
    : "Sync offline";

  const Icon = busy
    ? RefreshCw
    : enabled
    ? Cloud
    : error
    ? AlertTriangle
    : CloudOff;

  return (
    <button
      type="button"
      onClick={onClick}
      title={label}
      aria-label={label}
      className={`p-2.5 rounded-md border min-w-[40px] min-h-[40px] flex items-center justify-center hover-elevate ${enabled ? "border-primary/40 text-primary" : "border-card-border text-muted-foreground"}`}
      data-testid="sync-status"
    >
      <Icon className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} />
    </button>
  );
}
